import { create } from 'zustand';

import type { ChatMessage, ChatThread, ThreadSummary } from '../types';
import {
  sendChatMessageStream,
  listChatThreads,
  getChatThread,
  deleteChatThread,
  toggleThreadVisibility,
} from '../api/dashboard';

// Streaming state for real-time updates
interface StreamingState {
  isStreaming: boolean;
  statusMessage: string | null;
  streamingText: string;
  currentTool: {
    name: string;
    input: Record<string, unknown>;
  } | null;
}

interface ChatState {
  // Thread list
  threads: ThreadSummary[];
  publicThreads: ThreadSummary[];
  isLoadingThreads: boolean;

  // Current thread
  currentThread: ChatThread | null;
  currentThreadId: string | null;
  isLoadingThread: boolean;
  isCurrentThreadOwner: boolean;  // Whether current user owns the thread

  // Sending state
  isSending: boolean;
  pendingMessage: string | null;

  // Streaming state
  streaming: StreamingState;

  // Abort controller for cancelling streams
  abortController: AbortController | null;

  // Error state
  error: string | null;

  // Last failed message for retry
  lastFailedMessage: string | null;

  // Actions
  loadThreads: () => Promise<void>;
  loadThread: (threadId: string) => Promise<void>;
  sendMessage: (message: string, csContextSeed?: Record<string, unknown>) => Promise<void>;
  cancelStream: () => void;
  retryLastMessage: () => Promise<void>;
  startNewThread: () => void;
  deleteThread: (threadId: string) => Promise<void>;
  toggleVisibility: (threadId: string) => Promise<void>;
  clearError: () => void;
}

const initialStreamingState: StreamingState = {
  isStreaming: false,
  statusMessage: null,
  streamingText: '',
  currentTool: null,
};

export const useChatStore = create<ChatState>((set, get) => ({
  threads: [],
  publicThreads: [],
  isLoadingThreads: false,
  currentThread: null,
  currentThreadId: null,
  isLoadingThread: false,
  isCurrentThreadOwner: true,
  isSending: false,
  pendingMessage: null,
  streaming: initialStreamingState,
  abortController: null,
  error: null,
  lastFailedMessage: null,

  loadThreads: async () => {
    set({ isLoadingThreads: true, error: null });
    try {
      const response = await listChatThreads(50);
      set({
        threads: response.threads,
        publicThreads: response.publicThreads || [],
        isLoadingThreads: false,
      });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to load threads',
        isLoadingThreads: false,
      });
    }
  },

  loadThread: async (threadId: string) => {
    set({ isLoadingThread: true, error: null, currentThreadId: threadId });
    try {
      const thread = await getChatThread(threadId);
      // Determine if current user owns this thread
      // If createdByEmail is set, it's a public thread from another user
      const isOwner = !thread.createdByEmail;
      set({
        currentThread: thread,
        isLoadingThread: false,
        isCurrentThreadOwner: isOwner,
      });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to load thread',
        isLoadingThread: false,
        currentThread: null,
        isCurrentThreadOwner: true,
      });
    }
  },

  sendMessage: async (message: string, csContextSeed?: Record<string, unknown>) => {
    const { currentThreadId, currentThread } = get();

    // Create abort controller for this stream
    const abortController = new AbortController();

    // Optimistic update: add user message immediately
    const optimisticUserMessage: ChatMessage = {
      id: `temp-user-${Date.now()}`,
      role: 'user',
      content: message,
      createdAt: new Date().toISOString(),
      isCompacted: false,
    };

    // Add placeholder for streaming assistant message
    const streamingAssistantMessage: ChatMessage = {
      id: `temp-assistant-${Date.now()}`,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
      isCompacted: false,
    };

    set({
      isSending: true,
      pendingMessage: message,
      error: null,
      lastFailedMessage: null, // Clear any previous failed message
      abortController,
      streaming: {
        isStreaming: true,
        statusMessage: 'Connecting...',
        streamingText: '',
        currentTool: null,
      },
      currentThread: currentThread
        ? {
            ...currentThread,
            messages: [...currentThread.messages, optimisticUserMessage, streamingAssistantMessage],
          }
        : {
            id: 'temp',
            userId: '',
            messages: [optimisticUserMessage, streamingAssistantMessage],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            isArchived: false,
            isPublic: false,
          },
    });

    // Track collected data during streaming
    let finalThreadTitle = currentThread?.title || '';
    const toolMessages: ChatMessage[] = [];
    let streamedText = '';

    try {
      await sendChatMessageStream(
        message,
        currentThreadId || undefined,
        {
          onConnected: (threadId, isNewThread) => {
            set({
              currentThreadId: threadId,
              streaming: { ...get().streaming, statusMessage: null },
            });

            // If new thread, update the thread in state
            if (isNewThread) {
              const { currentThread: ct } = get();
              if (ct) {
                set({
                  currentThread: { ...ct, id: threadId },
                });
              }
            }
          },

          onStatus: (statusMessage) => {
            set({
              streaming: { ...get().streaming, statusMessage },
            });
          },

          onToolStart: (tool, input) => {
            // Add tool message to the conversation
            const toolMessage: ChatMessage = {
              id: `tool-${Date.now()}`,
              role: 'assistant',
              content: `Executing SQL: ${(input as { explanation?: string }).explanation || ''}`,
              toolName: tool,
              toolInput: input,
              createdAt: new Date().toISOString(),
              isCompacted: false,
            };
            toolMessages.push(toolMessage);

            // Update the thread to show the tool message
            const { currentThread: ct } = get();
            if (ct) {
              // Insert tool message before the streaming assistant message
              const messages = [...ct.messages];
              const streamingIdx = messages.findIndex(m => m.id.startsWith('temp-assistant-'));
              if (streamingIdx > -1) {
                messages.splice(streamingIdx, 0, toolMessage);
              } else {
                messages.push(toolMessage);
              }
              set({
                currentThread: { ...ct, messages },
                streaming: {
                  ...get().streaming,
                  currentTool: { name: tool, input },
                  statusMessage: 'Executing SQL query...',
                },
              });
            }
          },

          onToolResult: (_tool, data) => {
            // Update the last tool message with results
            const lastToolMessage = toolMessages[toolMessages.length - 1];
            if (lastToolMessage) {
              lastToolMessage.toolOutput = {
                rowCount: data.rowCount,
                columns: data.columns,
                executionTimeMs: data.executionTimeMs,
                truncated: data.truncated,
              };
              // Add chart config if available
              if (data.chart) {
                lastToolMessage.chartConfig = data.chart;
              }
            }

            // Update in the thread
            const { currentThread: ct } = get();
            if (ct) {
              const messages = ct.messages.map(m =>
                m.id === lastToolMessage?.id
                  ? { ...m, toolOutput: lastToolMessage.toolOutput, chartConfig: lastToolMessage.chartConfig }
                  : m
              );
              set({
                currentThread: { ...ct, messages },
                streaming: {
                  ...get().streaming,
                  currentTool: null,
                  statusMessage: 'Analyzing results...',
                },
              });
            }
          },

          onToolError: (_tool, error) => {
            // Update the last tool message with error
            const lastToolMessage = toolMessages[toolMessages.length - 1];
            if (lastToolMessage) {
              lastToolMessage.toolError = error;
            }

            // Update in the thread
            const { currentThread: ct } = get();
            if (ct) {
              const messages = ct.messages.map(m =>
                m.id === lastToolMessage?.id
                  ? { ...m, toolError: error }
                  : m
              );
              set({
                currentThread: { ...ct, messages },
                streaming: {
                  ...get().streaming,
                  currentTool: null,
                  statusMessage: null,
                },
              });
            }
          },

          onTextDelta: (content) => {
            streamedText += content;

            // Update the streaming assistant message
            const { currentThread: ct } = get();
            if (ct) {
              const messages = ct.messages.map(m =>
                m.id.startsWith('temp-assistant-')
                  ? { ...m, content: streamedText }
                  : m
              );
              set({
                currentThread: { ...ct, messages },
                streaming: {
                  ...get().streaming,
                  streamingText: streamedText,
                  statusMessage: null,
                },
              });
            }
          },

          onThreadTitle: (title) => {
            finalThreadTitle = title;
            const { currentThread: ct } = get();
            if (ct) {
              set({
                currentThread: { ...ct, title },
              });
            }
          },

          onDone: (threadId, userMessageId, assistantMessageId) => {
            // Finalize messages with real IDs
            const { currentThread: ct, threads: currentThreads } = get();
            if (ct) {
              const messages = ct.messages.map(m => {
                if (m.id.startsWith('temp-user-')) {
                  return { ...m, id: userMessageId };
                }
                if (m.id.startsWith('temp-assistant-')) {
                  return { ...m, id: assistantMessageId };
                }
                return m;
              });

              set({
                currentThread: { ...ct, id: threadId, messages },
                currentThreadId: threadId,
                isSending: false,
                pendingMessage: null,
                streaming: initialStreamingState,
                abortController: null,
              });
            }

            // Update thread list
            if (!currentThreadId) {
              // New thread - add to list
              const newThreadSummary: ThreadSummary = {
                id: threadId,
                title: finalThreadTitle,
                lastMessagePreview: message.slice(0, 100),
                messageCount: toolMessages.length + 2, // user + assistant + tools
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              };
              set({ threads: [newThreadSummary, ...currentThreads] });
            } else {
              // Update existing thread
              set({
                threads: currentThreads.map((t) =>
                  t.id === threadId
                    ? {
                        ...t,
                        title: finalThreadTitle || t.title,
                        lastMessagePreview: message.slice(0, 100),
                        updatedAt: new Date().toISOString(),
                      }
                    : t
                ),
              });
            }
          },

          onError: (error) => {
            set({
              error,
              isSending: false,
              pendingMessage: null,
              streaming: initialStreamingState,
              abortController: null,
              lastFailedMessage: message, // Save for retry
            });

            // Remove optimistic messages on error
            const { currentThread: ct } = get();
            if (ct) {
              set({
                currentThread: {
                  ...ct,
                  messages: ct.messages.filter(
                    m => !m.id.startsWith('temp-')
                  ),
                },
              });
            }
          },
        },
        abortController.signal,
        csContextSeed
      );
    } catch (err) {
      // Handle fetch errors or aborts
      if ((err as Error).name === 'AbortError') {
        // Stream was cancelled - clean up but don't show error
        set({
          isSending: false,
          pendingMessage: null,
          streaming: initialStreamingState,
          abortController: null,
        });
      } else {
        set({
          error: err instanceof Error ? err.message : 'Failed to send message',
          isSending: false,
          pendingMessage: null,
          streaming: initialStreamingState,
          abortController: null,
          lastFailedMessage: message, // Save for retry
        });

        // Remove optimistic messages on error
        const { currentThread: ct } = get();
        if (ct) {
          set({
            currentThread: {
              ...ct,
              messages: ct.messages.filter(
                m => !m.id.startsWith('temp-')
              ),
            },
          });
        }
      }
    }
  },

  cancelStream: () => {
    const { abortController } = get();
    if (abortController) {
      abortController.abort();
    }
  },

  retryLastMessage: async () => {
    const { lastFailedMessage, sendMessage } = get();
    if (lastFailedMessage) {
      set({ error: null, lastFailedMessage: null });
      await sendMessage(lastFailedMessage);
    }
  },

  startNewThread: () => {
    // Cancel any ongoing stream
    get().cancelStream();

    set({
      currentThread: null,
      currentThreadId: null,
      isCurrentThreadOwner: true,
      error: null,
      streaming: initialStreamingState,
    });
  },

  deleteThread: async (threadId: string) => {
    const { threads, currentThreadId } = get();
    try {
      await deleteChatThread(threadId);
      set({
        threads: threads.filter((t) => t.id !== threadId),
        // Clear current thread if it was deleted
        ...(currentThreadId === threadId
          ? { currentThread: null, currentThreadId: null }
          : {}),
      });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to delete thread',
      });
    }
  },

  toggleVisibility: async (threadId: string) => {
    const { threads, currentThread } = get();
    try {
      // Find current visibility state
      const thread = threads.find((t) => t.id === threadId);
      const currentIsPublic = thread?.isPublic || false;

      // Toggle visibility
      const result = await toggleThreadVisibility(threadId, !currentIsPublic);

      // Update threads list
      set({
        threads: threads.map((t) =>
          t.id === threadId ? { ...t, isPublic: result.isPublic } : t
        ),
        // Update current thread if it's the one being toggled
        ...(currentThread?.id === threadId
          ? { currentThread: { ...currentThread, isPublic: result.isPublic } }
          : {}),
      });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to update visibility',
      });
    }
  },

  clearError: () => set({ error: null }),
}));
